import { useState, useEffect } from 'react'
import { seoService } from '../../services/seoService'
import { useSeo } from '../../contexts/SeoContext'
import Card, { CardHeader, CardContent } from '../ui/Card'
import Button from '../ui/Button'
import Input from '../ui/Input'
import LoadingState from '../ui/LoadingState'
import { Globe, Save, AlertCircle, CheckCircle, Info } from 'lucide-react'

const emptyForm = {
  site_name: '',
  default_title: '',
  meta_description: '',
  meta_keywords: '',
  og_title: '',
  og_description: '',
  og_image_url: '',
  og_type: 'website',
  og_locale: 'es_ES',
  twitter_card: 'summary_large_image',
  twitter_site: '',
  canonical_base_url: '',
  theme_color: '#0f1623',
  robots: 'index, follow',
  include_organization_json_ld: true,
}

export default function SeoConfigPanel() {
  const { refreshSeo } = useSeo()
  const [form, setForm] = useState(emptyForm)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        const resp = await seoService.getPublic()
        if (cancelled) return
        if (resp?.success && resp.data) {
          setForm({ ...emptyForm, ...resp.data })
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message || 'Error al cargar SEO')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)
      const payload = {
        ...form,
        og_title: form.og_title?.trim() || null,
        og_description: form.og_description?.trim() || null,
        og_image_url: form.og_image_url?.trim() || null,
        meta_keywords: form.meta_keywords?.trim() || null,
        twitter_site: form.twitter_site?.trim() || null,
        canonical_base_url: form.canonical_base_url?.trim() || null,
        theme_color: form.theme_color?.trim() || null,
      }
      const resp = await seoService.updateAdmin(payload)
      if (resp?.success) {
        setSuccess('Configuración SEO guardada. Los visitantes verán los cambios al recargar.')
        if (resp.data) setForm({ ...emptyForm, ...resp.data })
        await refreshSeo()
        setTimeout(() => setSuccess(null), 5000)
      } else {
        setError(resp?.error || 'No se pudo guardar')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <LoadingState message="Cargando configuración SEO..." />
  }

  return (
    <div className="space-y-6 mt-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">SEO y redes sociales</h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Estos datos actualizan el título de la pestaña, la descripción en buscadores y las vistas previas al compartir
            el enlace (Open Graph / Twitter). Los cambios se aplican en la app al cargar la configuración desde el servidor.
          </p>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 flex gap-2 text-sm text-blue-900 dark:text-blue-200">
            <Info className="w-5 h-5 shrink-0 mt-0.5" />
            <span>
              Las redes sociales suelen leer las meta etiquetas en el <strong>primer HTML</strong> del servidor. Esta app es
              una SPA: tras el despliegue, conviene comprobar con{' '}
              <a
                className="underline font-medium"
                href="https://developers.facebook.com/tools/debug/"
                target="_blank"
                rel="noreferrer"
              >
                Facebook Sharing Debugger
              </a>{' '}
              o similar. El contenido dinámico mejora el SEO en navegación y compartidos desde la app abierta.
            </span>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex gap-2 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="w-5 h-5 shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex gap-2 text-sm text-green-800 dark:text-green-200">
              <CheckCircle className="w-5 h-5 shrink-0" />
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nombre del sitio (marca)"
                value={form.site_name}
                onChange={(e) => setForm((f) => ({ ...f, site_name: e.target.value }))}
                required
              />
              <Input
                label="Título por defecto (pestaña del navegador)"
                value={form.default_title}
                onChange={(e) => setForm((f) => ({ ...f, default_title: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Meta descripción (buscadores)
              </label>
              <textarea
                required
                rows={3}
                value={form.meta_description}
                onChange={(e) => setForm((f) => ({ ...f, meta_description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                maxLength={2000}
              />
            </div>
            <Input
              label="Palabras clave (opcional, separadas por comas)"
              value={form.meta_keywords || ''}
              onChange={(e) => setForm((f) => ({ ...f, meta_keywords: e.target.value }))}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="URL base canónica (sin barra final)"
                placeholder="https://manager.tusancestros.com"
                value={form.canonical_base_url || ''}
                onChange={(e) => setForm((f) => ({ ...f, canonical_base_url: e.target.value }))}
              />
              <Input
                label="Color de tema (PWA / barra)"
                value={form.theme_color || ''}
                onChange={(e) => setForm((f) => ({ ...f, theme_color: e.target.value }))}
              />
            </div>
            <Input
              label="Meta robots"
              value={form.robots || ''}
              onChange={(e) => setForm((f) => ({ ...f, robots: e.target.value }))}
            />

            <hr className="border-gray-200 dark:border-gray-700" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Open Graph (Facebook, LinkedIn, etc.)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="og:title (vacío = título de la página)"
                value={form.og_title || ''}
                onChange={(e) => setForm((f) => ({ ...f, og_title: e.target.value }))}
              />
              <Input
                label="og:type"
                value={form.og_type || 'website'}
                onChange={(e) => setForm((f) => ({ ...f, og_type: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                og:description (vacío = meta descripción)
              </label>
              <textarea
                rows={2}
                value={form.og_description || ''}
                onChange={(e) => setForm((f) => ({ ...f, og_description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
            <Input
              label="og:image (URL absoluta recomendada, mín. ~1200×630)"
              placeholder="https://..."
              value={form.og_image_url || ''}
              onChange={(e) => setForm((f) => ({ ...f, og_image_url: e.target.value }))}
            />
            <Input
              label="og:locale"
              value={form.og_locale || 'es_ES'}
              onChange={(e) => setForm((f) => ({ ...f, og_locale: e.target.value }))}
            />

            <hr className="border-gray-200 dark:border-gray-700" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Twitter / X</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Twitter card"
                value={form.twitter_card || 'summary_large_image'}
                onChange={(e) => setForm((f) => ({ ...f, twitter_card: e.target.value }))}
              />
              <Input
                label="Twitter @sitio (opcional)"
                placeholder="@tu_cuenta"
                value={form.twitter_site || ''}
                onChange={(e) => setForm((f) => ({ ...f, twitter_site: e.target.value }))}
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={Boolean(form.include_organization_json_ld)}
                onChange={(e) => setForm((f) => ({ ...f, include_organization_json_ld: e.target.checked }))}
                className="rounded border-gray-300"
              />
              Incluir JSON-LD Organization (datos estructurados para buscadores)
            </label>

            <div className="flex justify-end pt-2">
              <Button type="submit" loading={saving}>
                <Save className="w-4 h-4 mr-2" />
                Guardar SEO
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
